/**
 * Permission Context Provider
 *
 * Provides permission checking capabilities throughout the application.
 * Integrates with the backend V2 permission system and caches results.
 */

import { useQuery } from '@tanstack/react-query';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { checkMyPermissions } from '@/api/core/sdk.gen';
import type { PermissionCheckResultItem } from '@/api/core/types.gen';
import type { PermissionCheck, PermissionCheckResponse } from '@/types/permissions';

// Use the generated type for permission check results
type PermissionCheckResult = PermissionCheckResultItem;

import { generatePermissionKey } from '@/types/permissions';
import { useAuth } from './AuthContext';

interface PermissionContextValue {
  // Check single permission (cached) - conservative: returns false while loading
  checkPermission: (check: PermissionCheck) => boolean;

  // Check single permission with loading state info
  checkPermissionWithLoading: (check: PermissionCheck) => {
    hasPermission: boolean;
    isLoading: boolean;
  };

  // Check multiple permissions (cached)
  checkPermissions: (checks: PermissionCheck[]) => Record<string, boolean>;

  // Get full permission result with source info
  getPermissionResult: (check: PermissionCheck) => PermissionCheckResult | undefined;

  // Bulk check permissions and update cache
  bulkCheckPermissions: (checks: PermissionCheck[]) => Promise<PermissionCheckResponse>;

  // Permission loading state
  isLoading: boolean;

  // Error state
  error: Error | null;

  // Clear cached permissions (useful when permissions change)
  clearCache: () => void;
}

const PermissionContext = createContext<PermissionContextValue | null>(null);

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

interface PermissionProviderProps {
  children: React.ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const { token } = useAuth();
  const [cachedResults, setCachedResults] = useState<Record<string, PermissionCheckResult>>({});
  const [pendingChecks, setPendingChecks] = useState<PermissionCheck[]>([]);

  // Main query for bulk permission checking
  const { data, isLoading, error } = useQuery({
    queryKey: ['permissions', 'bulk', pendingChecks],
    queryFn: async () => {
      if (!token || pendingChecks.length === 0) {
        // Return empty result instead of throwing error
        return { data: { results: {}, checked_at: new Date().toISOString() } };
      }

      const response = await checkMyPermissions({
        body: { checks: pendingChecks },
        throwOnError: true,
      });
      return response;
    },
    enabled: !!token && pendingChecks.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes (matches backend cache TTL)
    retry: 2,
  });

  // Effect to handle successful data fetching
  useEffect(() => {
    if (data) {
      // Cache the results
      setCachedResults((prev) => ({
        ...prev,
        ...(data.data.results as unknown as Record<string, PermissionCheckResult>),
      }));
      // Clear pending checks
      setPendingChecks([]);
    }
  }, [data]);

  // Effect to handle query errors - clear pending checks to prevent infinite loading
  useEffect(() => {
    if (error) {
      console.error('Permission check error:', error);
      // Clear pending checks on error to prevent infinite loading state
      setPendingChecks([]);
    }
  }, [error]);

  // Failsafe: Clear pending checks after a timeout to prevent infinite loading
  useEffect(() => {
    if (pendingChecks.length > 0) {
      const timeout = setTimeout(() => {
        console.warn('Permission checks taking too long, clearing pending checks');
        setPendingChecks([]);
      }, 10000); // 10 second timeout (restored to original)

      return () => clearTimeout(timeout);
    }
  }, [pendingChecks]);

  // Check single permission
  const checkPermission = useCallback(
    (check: PermissionCheck): boolean => {
      const key = generatePermissionKey(check);
      const result = cachedResults[key];

      if (result !== undefined) {
        return result.has_permission;
      }

      // Check if this permission is already pending
      const alreadyPending = pendingChecks.some(
        (existingCheck) => generatePermissionKey(existingCheck) === key
      );

      // If not cached and not already pending, queue it
      if (!alreadyPending) {
        setTimeout(() => {
          setPendingChecks((prev) => {
            const exists = prev.some(
              (existingCheck) => generatePermissionKey(existingCheck) === key
            );
            if (!exists) {
              return [...prev, check];
            }
            return prev;
          });
        }, 0);
      }

      // Return false while loading (conservative approach)
      return false;
    },
    [cachedResults, pendingChecks]
  );

  // Check multiple permissions
  const checkPermissions = useCallback(
    (checks: PermissionCheck[]): Record<string, boolean> => {
      const results: Record<string, boolean> = {};
      const uncachedChecks: PermissionCheck[] = [];

      for (const check of checks) {
        const key = generatePermissionKey(check);
        const result = cachedResults[key];

        if (result !== undefined) {
          results[key] = result.has_permission;
        } else {
          results[key] = false; // Conservative default
          uncachedChecks.push(check);
        }
      }

      // Queue uncached checks for batch request (defer to avoid setState during render)
      if (uncachedChecks.length > 0) {
        setTimeout(() => {
          setPendingChecks((prev) => {
            const combined = [...prev];
            for (const check of uncachedChecks) {
              const key = generatePermissionKey(check);
              const exists = combined.some(
                (existingCheck) => generatePermissionKey(existingCheck) === key
              );
              if (!exists) {
                combined.push(check);
              }
            }
            return combined;
          });
        }, 0);
      }

      return results;
    },
    [cachedResults]
  );

  // Get full permission result with source info
  const getPermissionResult = useCallback(
    (check: PermissionCheck): PermissionCheckResult | undefined => {
      const key = generatePermissionKey(check);
      return cachedResults[key];
    },
    [cachedResults]
  );

  // Bulk check permissions and update cache
  const bulkCheckPermissions = useCallback(
    async (checks: PermissionCheck[]): Promise<PermissionCheckResponse> => {
      if (!token) {
        throw new Error('No authentication token available');
      }

      const { data } = await checkMyPermissions({
        body: { checks },
        throwOnError: true,
      });

      // Update cache with new results
      setCachedResults((prev) => ({
        ...prev,
        ...(data.results as unknown as Record<string, PermissionCheckResult>),
      }));

      // Convert to PermissionCheckResponse format
      return {
        results: data.results as unknown as Record<string, PermissionCheckResult>,
        checked_at: data.checked_at,
        cache_ttl_seconds: data.cache_ttl_seconds,
      };
    },
    [token]
  );

  // Check single permission with loading state info
  const checkPermissionWithLoading = useCallback(
    (check: PermissionCheck): { hasPermission: boolean; isLoading: boolean } => {
      const key = generatePermissionKey(check);
      const result = cachedResults[key];

      if (result !== undefined) {
        return { hasPermission: result.has_permission, isLoading: false };
      }

      // Check if this permission is already pending
      const alreadyPending = pendingChecks.some(
        (existingCheck) => generatePermissionKey(existingCheck) === key
      );

      // If not cached and not already pending, queue it
      if (!alreadyPending) {
        setTimeout(() => {
          setPendingChecks((prev) => {
            const exists = prev.some(
              (existingCheck) => generatePermissionKey(existingCheck) === key
            );
            if (!exists) {
              return [...prev, check];
            }
            return prev;
          });
        }, 0);
      }

      // Return loading state - let component decide how to handle
      return { hasPermission: false, isLoading: true };
    },
    [cachedResults, pendingChecks]
  );

  // Clear cached permissions
  const clearCache = useCallback(() => {
    setCachedResults({});
    setPendingChecks([]);
  }, []);

  const value: PermissionContextValue = {
    checkPermission,
    checkPermissionWithLoading,
    checkPermissions,
    getPermissionResult,
    bulkCheckPermissions,
    isLoading: isLoading || pendingChecks.length > 0, // Include pending checks in loading state
    error: error as Error | null,
    clearCache,
  };

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};
